<template>
  <div id="app" class="h-100">
    <router-view
      v-if="!$store.state.app.waitingForConnection"
      :class="isHubUser ? 'float-right' : ''"
      :style="{ width: appContainerWidth }"
    />
    <offline v-else />
    <sidebar-widget
      :user-id="hubUserTransmissibleId"
      ref="sidebarWidget"
      container-position="relative"
      v-if="isHubUser"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { Offline } from 'common-views'
import { hideToastMixin } from '@common/mixins/toaster/hideToast'
import { ubyHubMixin } from '@common/mixins/ubyHubMixin'
import { keycloakMixin } from '@mixins/keycloakMixin'

// HACK: widget positioning for burger arrow is not suited to our context,
// we have to override the full style with injected JS in custom element shadowRoot in order to make it appear on UI ...
// we can't unfortunately inject only the modified CSS and we have to C/P all CSS of #burger element from original widget code.
const overrideHubBurgerArrowStyle = `position: absolute; top: 0; left: -10px; right: auto; z-index: 100; width: 50px; height: 50px; transition: none; display: flex; justify-content: center;`

export default {
  name: 'App',
  components: {
    Offline,
  },
  mixins: [hideToastMixin, ubyHubMixin, keycloakMixin],
  data() {
    return {
      // default when hub sidebar not loaded
      hubSidebarWidth: '0px',
      isDrawingSidebarWidget: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['hubUserTransmissibleId']),
    // HACK: cannot be written inside auth store getters because it relies on another getter to evaluate correctly
    isHubUser() {
      return Boolean(this.hubUserTransmissibleId)
    },
    locale() {
      return this.$i18n.locale
    },
    online() {
      return this.$store.state.app.online
    },
    appContainerWidth() {
      return `calc(100% - ${this.hubSidebarWidth})`
    },
  },
  methods: {
    async drawHubSidebarWidget() {
      // race-condition guard : sometimes mounted & isHubUser watchers trigger at the same time
      this.isDrawingSidebarWidget = true

      // load uby hub component into DOM,
      // IF not yet loaded into customElementRegistry: https://developer.mozilla.org/en-US/docs/Web/API/CustomElementRegistry/get
      // TODO fix console error  "vue component name not registered" when using customElement ?
      if (!this.customElementRegistry.get('sidebar-widget')) {
        const sideBarWidgetElement = await this.initHub()

        if (sideBarWidgetElement) {
          // TODO: we need to be kept informed of updated sidebar status ...
          // for now, widget doesn't trigger events when collapsed / open so we fix its width
          // and it will appear above app container when opened
          this.hubSidebarWidth = this.getHubSidebarWidth()

          // HACK: set inner CSS to fix hamburger arrow positioning
          // https://stackoverflow.com/questions/37352637/shadow-dom-styling-from-the-outside
          sideBarWidgetElement.shadowRoot
            .querySelector('#sidebar-container #burger')
            .setAttribute('style', overrideHubBurgerArrowStyle)
        } else {
          this.$log.warn(
            '[App.drawSidebarWidget] unable to fetch sidebar widget from remote URL. skipping.',
          )
        }
      } else {
        this.$log.debug(
          '[App.drawSidebarWidget] sidebar widget already loaded into component registry. skipping.',
        )
      }

      this.isDrawingSidebarWidget = false
    },
  },
  watch: {
    locale(newValue) {
      if (newValue) {
        localStorage.setItem('locale', this.locale)
      }
    },
    async isHubUser(newValue, oldValue) {
      if (newValue && newValue !== oldValue && !this.isDrawingSidebarWidget) {
        console.debug(
          '[App.isHubUser] changed, trying to draw sidebar widget...',
        )
        await this.drawHubSidebarWidget()
      }
    },
  },
  async mounted() {
    /**
     * APPLICATION BOOTSTRAP -- refactor this in a service if necessary.
     * TODO: it seems we sometimes loose the connection to keycloak / kuzzle sockets because events are NOT
     * triggered in due time, such that we have to manually cater for token refresh upon user navigation in router.
     * investigate & fix if possible.
     */
    if (this.$keycloak) {
      this.$keycloak.onReady = (authenticated) => {
        this.$log.debug(
          '[App.mounted][keycloakOnReadyEvent] KC client ready:',
          authenticated,
        )
      }

      this.$keycloak.onAuthSuccess = async () => {
        this.$log.debug('[App.mounted][keycloakOnAuthSuccessEvent] KC auth OK.')
      }

      this.$keycloak.onAuthRefreshSuccess = async () => {
        this.$log.debug(
          '[App.mounted][keycloakOnAuthRefreshSuccessEvent] KC refresh auth OK.',
        )
      }

      this.$keycloak.onAuthError = async (authError) => {
        this.$log.error(
          '[App.mounted][keycloakOnAuthErrorEvent] Error during KC authentication:',
          authError,
        )

        // should be handled by router authGuard, do not duplicate logout behavior here
        // keycloakMixin
        // await this.logout()
      }

      this.$keycloak.onTokenExpired = async () => {
        this.$log.warn(
          '[App.mounted][keycloakOnTokenExpiredEvent] KC token expired, trying to refresh KC token...',
        )

        // keycloakMixin
        await this.refreshToken()
      }

      this.$keycloak.onAuthLogout = async () => {
        this.$log.debug(
          '[App.mounted][keycloakOnAuthLogoutEvent] KC logout event.',
        )

        // probably expected due to either user action or invalid auth flow, do nothing.
      }
    }

    this.$kuzzle.addListener('connected', () => {
      this.$store.commit('app/SET_ONLINE')
    })

    this.$kuzzle.addListener('reconnected', () => {
      this.$store.commit('app/SET_ONLINE')
    })

    this.$kuzzle.addListener('disconnected', (event) => {
      if (event.origin !== 'websocket/auth-renewal') {
        this.$store.commit('app/SET_OFFLINE')
      }
    })

    //TODO when is this triggered ?
    this.$kuzzle.addListener('tokenExpired', async () => {
      console.warn(
        '[App.kuzzleTokenExpiredListener] tokenExpired, trying to refresh kuzzle token ...',
      )

      // try to re-trigger kuzzle auth in the background.
      // TODO: should we call refreshToken ? it is documented as only possible before token expires.
      // afterwards, it seems we can use both according to the docs:
      // https://docs.kuzzle.io/core/2/guides/main-concepts/authentication/#authentication-token
      try {
        await this.$store.dispatch('auth/KUZZLE_LOGIN', {
          kuzzle: this.$kuzzle,
          strategy: 'keycloak',
          keycloak: this.$keycloak,
        })
      } catch (kuzzleLoginError) {
        console.error(
          '[App.kuzzleTokenExpiredListener] failure re-authenticating to kuzzle. triggering logout.',
          kuzzleLoginError,
        )

        await this.logout()
      }
    })

    const persistedLocale = localStorage.getItem('locale')
    if (persistedLocale) {
      this.$i18n.locale = persistedLocale
    }

    await this.$kuzzle.connect()

    if (this.isHubUser && !this.isDrawingSidebarWidget) {
      this.$log.debug(
        '[App.mounted] hub user detected, trying to draw sidebar widget...',
      )
      await this.drawHubSidebarWidget()
    }
  },
}
</script>

<style lang="scss">
// CoreUI Icons Set
@import '~@coreui/icons/css/all.min.css';
/* Import Font Awesome Icons Set */
$fa-font-path: '~font-awesome/fonts/';
@import '~@fortawesome/fontawesome-free/css/all.min.css';
/* Import Simple Line Icons Set */
$simple-line-font-path: '~simple-line-icons/fonts/';
@import '~simple-line-icons/scss/simple-line-icons.scss';
/* Import Bootstrap Vue Styles */
@import '~bootstrap-vue/dist/bootstrap-vue.css';
// Import Main styles for this application
@import '@common/assets/styles/scss/style';

#app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}
#nav {
  padding: 30px;
  a {
    font-weight: bold;
    color: #2c3e50;
    &.router-link-exact-active {
      color: #42b983;
    }
  }
}

// HACK: override widget style to make sure hamburger arrow is shown even when app-container is not at same position
// #sidebar-container #burger {
//   position: absolute;
//   top: '0px';
//   left: '10px';
// }
</style>
